import type { Expense } from "../../components/steps/expense/expenseTypes";
import type { LoanApplicationData } from "../../hooks/useNbiForm";
import type { SetFieldValueCallback } from "./models";

const boolToYesNo = (val: boolean | null | undefined): string => {
    if (val === true) return "yes";
    if (val === false) return "no";
    return "";
};

const parseAmount = (val: string | number | null | undefined): number => {
    if (val == null) return 0;
    return parseFloat(String(val)) || 0;
};

// Use category+id as the stable frontend ID to avoid collisions when the
// backend reuses numeric IDs across different expense categories.
const makeFrontendId = (backendId: number | string, category: string): string =>
    `${category}_${backendId}`;

export function handleExpenseDocument(
    expenseData: any,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback
): void {
    const expenseCategory: string = expenseData.expenseCategory ?? "";
    const expenseType: string = expenseData.expenseType ?? "";
    const applicantId = expenseData.applicantId != null ? String(expenseData.applicantId) : "";
    const frontendId = makeFrontendId(expenseData.id, expenseCategory);

    const existingExpenses: Expense[] = (currentFormData as any).expenses || [];
    const existingIndex = existingExpenses.findIndex((e: any) => e.id === frontendId);

    let newExpense: Expense | null = null;

    const currency: string = expenseData.currency || "AUD";
    const currencyCountry: string = expenseData.currencyCountry || "";

    if (expenseCategory === "living_expense") {
        if (expenseType === "general_living") {
            newExpense = {
                id: frontendId,
                expenseType: "General Living Expenses",
                applicantId,
                frequency: expenseData.frequency || "",
                totalAmount: parseAmount(expenseData.totalAmount),
                currency,
                currencyCountry,
            };
        } else if (expenseType === "childcare") {
            newExpense = {
                id: frontendId,
                expenseType: "Childcare Costs",
                applicantId,
                frequency: expenseData.frequency || "",
                totalAmount: parseAmount(expenseData.totalAmount),
                currency,
                currencyCountry,
            };
        } else if (expenseType === "vehicle_costs") {
            newExpense = {
                id: frontendId,
                expenseType: "Vehicle Running Costs",
                applicantId,
                vehicleCostsInPreTaxDeductions: boolToYesNo(expenseData.preTaxDeduction),
                frequency: expenseData.frequency || "",
                totalAmount: parseAmount(expenseData.totalAmount),
                currency,
                currencyCountry,
            };
        } else if (expenseType === "rental_board") {
            newExpense = {
                id: frontendId,
                expenseType: "Rental / Board Expenses",
                applicantId,
                monthlyAmount: parseAmount(expenseData.monthlyRentalAmount),
                residesInCompanyOrTrustProperty: boolToYesNo(expenseData.companyTrustProperty),
                currency,
                currencyCountry,
            };
        }
    } else if (expenseCategory === "discretionary_expense") {
        if (expenseType === "investment_property") {
            newExpense = {
                id: frontendId,
                expenseType: "Investment Property Expense",
                applicantId,
                propertyReference: expenseData.propertyReference || "",
                expenseDescription: expenseData.description || "",
                frequency: expenseData.frequency || "",
                totalAmount: parseAmount(expenseData.totalAmount),
                isSolelyApplicantResponsibility: boolToYesNo(expenseData.soleResponsibility),
                applicantSharePercentage: parseAmount(expenseData.percentage),
                currency,
                currencyCountry,
            };
        } else if (expenseType === "other_non_benchmark") {
            newExpense = {
                id: frontendId,
                expenseType: "Other Non-Benchmark Expenses",
                applicantId,
                expenseDescription: expenseData.description || "",
                frequency: expenseData.frequency || "",
                totalAmount: parseAmount(expenseData.totalAmount),
                isSolelyApplicantResponsibility: boolToYesNo(expenseData.soleResponsibility),
                ownershipPercentage: parseAmount(expenseData.percentage),
                currency,
                currencyCountry,
            };
        }
    } else if (expenseCategory === "tax_arrears_expense") {
        newExpense = {
            id: frontendId,
            expenseType: "ATO Tax Arrears",
            applicantId,
            totalAmount: parseAmount(expenseData.totalAmount),
            hasRepaymentArrangement: boolToYesNo(expenseData.hasRepaymentArrangement),
            allRepaymentsPaidToDate: boolToYesNo(expenseData.repaymentsCurrent),
            monthlyRepaymentAmount: parseAmount(expenseData.monthlyRepaymentAmount),
            currency,
            currencyCountry,
        };
    } else if (expenseCategory === "planning_expense") {
        if (expenseType === "financial_position_changes") {
            newExpense = {
                id: frontendId,
                expenseType: "Expected Financial Position Changes",
                applicantId,
                expectedChangesDescription: expenseData.expectedChangesDescription || "",
            };
        } else if (expenseType === "retirement_related") {
            newExpense = {
                id: frontendId,
                expenseType: "Retirement-Related Expenses",
                applicantId,
                useCoApplicantIncome: boolToYesNo(expenseData.useCoapplicantIncome),
                postRetirementStrategy: expenseData.postRetirementStrategy || "",
            };
        }
    }

    if (!newExpense) return;

    let updatedExpenses: Expense[];
    if (existingIndex >= 0) {
        updatedExpenses = [...existingExpenses];
        updatedExpenses[existingIndex] = newExpense;
    } else {
        updatedExpenses = [...existingExpenses, newExpense];
    }

    setFieldValue("expenses" as keyof LoanApplicationData, updatedExpenses);
}
